// src/layouts/Layout.tsx

import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { msalInstance, ensureMsalInitialized } from '@/utils/msal';
import { loginRequest } from '@/config/authConfig';
import { TabProvider } from '@/context/TabContext';
import { AliveScope } from 'react-activation';
import SortableTabs from '@/components/PageTabs';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * Fetch current user info from Azure AD.
 */
const fetchUserInfo = async (): Promise<API.CurrentUser | undefined> => {
  try {
    await ensureMsalInitialized();
    const response = await msalInstance.handleRedirectPromise();
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      console.warn('No active MSAL account found.');
      return undefined;
    }

    if (response) {
      console.log('MSAL Redirect Response received.');
    }

    // Use tokenManager to get valid token (will refresh if needed)
    const { getValidToken } = await import('./utils/tokenManager');
    await getValidToken();

    return {
      name: accounts[0].name || 'User',
      email: accounts[0].username,
      avatar: '/default-avatar.png',
    };
  } catch (error) {
    console.error('Failed to fetch user info:', error);
    const { pathname } = history.location;
    if (pathname !== loginPath) {
      history.replace(loginPath); // Only redirect if not already on login page
    }
    return undefined;
  }
};

/**
 * Initialize global state.
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const { pathname } = history.location;

  if (pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/**
 * Configure ProLayout behavior at runtime.
 */
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => <AvatarDropdown>{avatarChildren}</AvatarDropdown>,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        console.warn('User not logged in, redirecting to login page.');
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    childrenRender: (children) => (
      <AliveScope>
        <TabProvider>
          <SortableTabs>{children}</SortableTabs>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((prevState) => ({
                  ...prevState,
                  settings,
                }));
              }}
            />
          )}
        </TabProvider>
      </AliveScope>
    ),
    ...initialState?.settings,
  };
};
